import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Close from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Reusable Country Filter Dropdown Component
 * Used across multiple pages (OrgList, InactiveUsers, etc.)
 */
const CountryFilterDropdown = ({ 
  countries = [], 
  selectedCountries = [], 
  onApply,
  buttonLabel,
  minWidth = "220px",
}) => {
  const [tempSelectedCountries, setTempSelectedCountries] = useState(selectedCountries);
  const [countrySearch, setCountrySearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const anchorRef = useRef(null);

  // Update temp selection when selected countries change from outside
  React.useEffect(() => {
    if (!dropdownOpen) {
      setTempSelectedCountries(selectedCountries);
    }
  }, [selectedCountries, dropdownOpen]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countries || countries.length === 0) return [];
    if (!countrySearch) return countries;
    return countries.filter((country) =>
      country.toLowerCase().includes(countrySearch.toLowerCase())
    );
  }, [countries, countrySearch]);

  // Handle country selection (temporary)
  const handleCountryToggle = (country) => {
    setTempSelectedCountries((prev) => {
      const newSelection = prev.includes(country)
        ? prev.filter((c) => c !== country)
        : [...prev, country];
      // Don't allow deselecting all countries
      return newSelection.length === 0 ? prev : newSelection;
    });
  };

  // Handle select all/deselect all
  const handleSelectAll = () => {
    if (tempSelectedCountries.length === countries?.length) {
      // Don't allow deselecting all - keep at least one (first country)
      if (countries?.length > 0) {
        setTempSelectedCountries([countries[0]]);
      }
    } else {
      setTempSelectedCountries(countries || []);
    }
  };

  // Handle close dropdown (cancel changes)
  const handleCancelDropdown = () => {
    setTempSelectedCountries(selectedCountries);
    setDropdownOpen(false);
    setCountrySearch("");
  };

  // Handle apply selection (Done button)
  const handleApplySelection = () => {
    onApply(tempSelectedCountries);
    setDropdownOpen(false);
    setCountrySearch("");
  };

  // Don't render if only 0 or 1 country
  if (!countries || countries.length <= 1) return null;

  // Generate button label
  const getButtonLabel = () => {
    if (buttonLabel) return buttonLabel;
    
    if (selectedCountries.length === 0) {
      return "Select countries";
    }
    if (selectedCountries.length === countries.length) {
      return "All Countries";
    }
    return `${selectedCountries.length} ${selectedCountries.length === 1 ? 'country' : 'countries'}`;
  };

  return (
    <Box sx={{ minWidth }}>
      <Typography
        variant="caption"
        sx={{
          color: "#888",
          fontSize: "0.75rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.8px",
          display: "block",
          marginBottom: "10px",
        }}
      >
        Filter Countries
      </Typography>
      <Button
        ref={anchorRef}
        onClick={() => {
          setTempSelectedCountries(selectedCountries);
          setDropdownOpen(!dropdownOpen);
        }}
        endIcon={<ExpandMoreIcon />}
        variant="outlined"
        fullWidth
        sx={{
          backgroundColor: "#fafafa",
          borderColor: "#e0e0e0",
          color: "#555",
          textTransform: "none",
          justifyContent: "space-between",
          height: "40px",
          fontSize: "0.875rem",
          "&:hover": {
            backgroundColor: "#f0f0f0",
            borderColor: "#e0e0e0",
          },
        }}
      >
        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
          {getButtonLabel()}
        </Typography>
      </Button>

      <Popper
        open={dropdownOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
      >
        <ClickAwayListener onClickAway={() => dropdownOpen && handleCancelDropdown()}>
          <Paper
            elevation={8}
            sx={{
              mt: 0.5,
              maxHeight: 400,
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px",
            }}
          >
            {/* Search Input */}
            <Box sx={{ p: 2, pb: 1 }}>
              <TextField
                size="small"
                placeholder="Search countries..."
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: countrySearch && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setCountrySearch("")}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                  },
                }}
              />
            </Box>

            {/* Select All / Deselect All */}
            <Box
              sx={{
                px: 2,
                pb: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Button
                size="small"
                onClick={handleSelectAll}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  color: "#1565c0",
                }}
              >
                {tempSelectedCountries.length === countries.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
              <Typography variant="caption" sx={{ color: "#666" }}>
                {tempSelectedCountries.length} / {countries.length}
              </Typography>
            </Box>

            {/* Countries List */}
            <Box
              sx={{
                maxHeight: 250,
                overflow: "auto",
                px: 1,
              }}
            >
              {filteredCountries.length === 0 ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    No countries found
                  </Typography>
                </Box>
              ) : (
                filteredCountries.map((country) => (
                  <MenuItem
                    key={country}
                    onClick={() => handleCountryToggle(country)}
                    sx={{
                      borderRadius: "6px",
                      mx: 0.5,
                      my: 0.25,
                    }}
                  >
                    <Checkbox
                      checked={tempSelectedCountries.includes(country)}
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={country}
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                      }}
                    />
                  </MenuItem>
                ))
              )}
            </Box>

            {/* Footer with Done button */}
            <Box
              sx={{
                p: 1.5,
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                size="small"
                variant="contained"
                onClick={handleApplySelection}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#1565c0",
                  "&:hover": {
                    backgroundColor: "#0d47a1",
                  },
                }}
              >
                Done
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default CountryFilterDropdown;
